import { ColumnFilterMap, ColumnFilter } from 'util/filtering/models';

export type SortDirection = 'asc' | 'desc';

export type SortOption = {
  field: string;
  direction: SortDirection;
};

export type AdvancedFilterQueryState = {
  columnFilters: ColumnFilterMap;
  sort?: SortOption;
};

const multiKeys = [
  'last4',
  'userId',
  'merchantCategory',
  'merchantName',
  'cardGroupId',
  'projectGroupId',
  'receiptStatus',
  'agaveTransactionStatus',
  'agaveGlCodeId',
  'agaveProjectId',
  'agaveSubJobId',
  'agaveCostCodeId',
  'agaveCostTypeId',
  // SEE-1135 — per-transaction Billable Item filter (Sage Intacct, opted-in)
  'erpBillableItemId',
  // SEE-974 — Vista equipment coding filters
  'agaveEquipmentId',
  'agaveEmCostCodeId',
  'agaveEmCostTypeId',
  // SEE-974 — advanced "Transaction type" pick (GL/PROJECT/EQUIPMENT) so it
  // survives refresh / shared link like the coding-id filters beside it.
  'codingType',
  'flexCodeTransactionStatus',
  'flexCodeId',
  'flexCostCodeId',
  'flexCostTypeId',
  'flexVendorId',
  'flexGlCodeId',
  'status',
  'agaveVendorId',
  'agaveLocationId',
  'agaveDepartmentId',
  'agaveSubaccountId',
];

// Discrete params carry only ids, so exclude-mode ("all except") needs its own
// marker or a shared link restores as include-mode. Absent = noExcept.
const EXCLUDE_RULE = 'allExcept';
const ruleParamFor = (key: string) => `${key}Rule`;

/**
 * Creates a stable, deterministic JSON string by sorting object keys recursively.
 */
export function stableStringify(value: unknown): string {
  const cache = new WeakSet();
  const stringify = (val: unknown): unknown => {
    if (val === null || typeof val !== 'object') return val;
    if (cache.has(val as object)) return undefined;
    cache.add(val as object);

    if (Array.isArray(val)) return (val as unknown[]).map((item) => stringify(item));

    const sortedObj: Record<string, unknown> = {};
    Object.keys(val as Record<string, unknown>)
      .sort()
      .forEach((key) => {
        sortedObj[key] = stringify((val as Record<string, unknown>)[key]);
      });
    return sortedObj;
  };

  return JSON.stringify(stringify(value));
}

export function deepEqual(a: unknown, b: unknown): boolean {
  try {
    return stableStringify(a) === stableStringify(b);
  } catch {
    return false;
  }
}

/**
 * Removes inactive/empty filters so URLs stay compact and readable.
 */
export function compactColumnFilters(columnFilters: ColumnFilterMap): ColumnFilterMap {
  const result: ColumnFilterMap = {} as ColumnFilterMap;
  if (!columnFilters) return result;

  Object.keys(columnFilters).forEach((key) => {
    const f = columnFilters[key];
    const hasValues = !!f.selectedValues?.length;
    const hasRange = f.minNumber !== undefined || f.maxNumber !== undefined;
    const isActive = f.active === true;
    if (hasValues || hasRange || isActive) {
      result[key] = {
        ...f,
        // Keep only the fields we can safely round-trip
        selectedValues: f.selectedValues || [],
        minNumber: f.minNumber,
        maxNumber: f.maxNumber,
        active: isActive,
      };
    }
  });

  return result;
}

export function isEmptyColumnFilters(columnFilters: ColumnFilterMap | undefined): boolean {
  if (!columnFilters) return true;
  const compacted = compactColumnFilters(columnFilters);
  return Object.keys(compacted).length === 0;
}

export function hashAdvancedFilterState(state: AdvancedFilterQueryState): string {
  return stableStringify(state);
}

/**
 * Serializes advanced filter state for use in a query parameter string value.
 * The output is URI-encoded JSON. This is human-readable and adequate unless
 * URLs become too long, in which case a more compact encoding can be added.
 */
export function serializeAdvancedFilters(state: AdvancedFilterQueryState): string {
  const payload: AdvancedFilterQueryState = {
    columnFilters: compactColumnFilters(state.columnFilters),
    sort: state.sort,
  };

  return encodeURIComponent(JSON.stringify(payload));
}

/**
 * Deserializes a query parameter value back into advanced filter state.
 * Returns null on parse/validation failure.
 */
export function deserializeAdvancedFilters(paramValue: string): AdvancedFilterQueryState | null {
  if (!paramValue) return null;
  try {
    const raw = JSON.parse(decodeURIComponent(paramValue));
    if (!raw || typeof raw !== 'object') return null;

    type ParsedPayload = { columnFilters?: ColumnFilterMap; sort?: SortOption };
    const payload = raw as ParsedPayload;
    const columnFilters = payload.columnFilters;
    const sort = payload.sort;
    if (!columnFilters || typeof columnFilters !== 'object') return null;

    return { columnFilters, sort };
  } catch {
    return null;
  }
}

// Discrete param mode (non-JSON) — focused support for a subset of filters

export type DiscreteParamConfig = {
  merchantNameParam?: string; // default 'merchantName' (multi)
  approvedAmountMinParam?: string; // default 'approvedAmountMin'
  approvedAmountMaxParam?: string; // default 'approvedAmountMax'
  sortFieldParam?: string; // optional, default 'sortField'
  sortDirParam?: string; // optional, default 'sortDir'
};

export const defaultDiscreteParamConfig: Required<DiscreteParamConfig> = {
  merchantNameParam: 'merchantName',
  approvedAmountMinParam: 'approvedAmountMin',
  approvedAmountMaxParam: 'approvedAmountMax',
  sortFieldParam: 'sortField',
  sortDirParam: 'sortDir',
};

/**
 * Reads discrete query params into an AdvancedFilterQueryState supporting:
 * - merchantName as repeated params or a single comma-delimited list
 * - approvedAmountValue via min/max numeric params
 * - optional sortField/sortDir
 */
export function deserializeDiscreteParams(
  params: URLSearchParams,
  cfg?: DiscreteParamConfig
): AdvancedFilterQueryState | null {
  const { merchantNameParam, approvedAmountMinParam, approvedAmountMaxParam, sortFieldParam, sortDirParam } =
    {
      ...defaultDiscreteParamConfig,
      ...(cfg || {}),
    };

  const columnFilters: ColumnFilterMap = {} as ColumnFilterMap;

  // merchantName (multi)
  const merchantVals = params.getAll(merchantNameParam);
  let merchantNames: string[] = [];
  if (merchantVals.length > 1) {
    merchantNames = merchantVals.filter((v) => v !== '');
  } else if (merchantVals.length === 1) {
    const single = merchantVals[0];
    if (single.includes(',')) {
      merchantNames = single
        .split(',')
        .map((s) => s.trim())
        .filter((v) => v !== '');
    } else if (single) {
      merchantNames = [single];
    }
  }
  if (merchantNames.length) {
    columnFilters.merchantName = {
      key: 'merchantName',
      type: 'string',
      name: 'merchantName',
      options: [],
      selectedValues: merchantNames.map((name) => ({ id: name, label: name })),
      rule: 'noExcept',
      page: '',
      active: true,
    } as ColumnFilter;
  }

  // approvedAmountValue (range)
  const minStr = params.get(approvedAmountMinParam);
  const maxStr = params.get(approvedAmountMaxParam);
  const min = minStr !== null && minStr !== '' ? Number(minStr) : undefined;
  const max = maxStr !== null && maxStr !== '' ? Number(maxStr) : undefined;
  const hasMin = typeof min === 'number' && !Number.isNaN(min);
  const hasMax = typeof max === 'number' && !Number.isNaN(max);
  if (hasMin || hasMax) {
    columnFilters.approvedAmountValue = {
      key: 'approvedAmountValue',
      type: 'number',
      name: 'approvedAmountValue',
      options: [],
      selectedValues: [],
      rule: 'range',
      page: '',
      active: true,
      minNumber: hasMin ? (min as number) : undefined,
      maxNumber: hasMax ? (max as number) : undefined,
    } as ColumnFilter;
  }

  // Generic multi-value ids: use filter key as the param name
  const parseMulti = (key: string): string[] => {
    const vals = params.getAll(key);
    if (vals.length > 1) return vals.filter((v) => v !== '');
    if (vals.length === 1) {
      const single = vals[0];
      if (!single) return [];
      if (single.includes(','))
        return single
          .split(',')
          .map((s) => s.trim())
          .filter((v) => v !== '');
      return [single];
    }
    return [];
  };

  multiKeys.forEach((key) => {
    const values = parseMulti(key);
    if (values.length) {
      columnFilters[key] = {
        key,
        type: 'string',
        name: key,
        options: [],
        selectedValues: values.map((id) => ({ id, label: id })),
        rule: params.get(ruleParamFor(key)) === EXCLUDE_RULE ? EXCLUDE_RULE : 'noExcept',
        page: '',
        active: true,
      } as ColumnFilter;
    }
  });

  // sort (optional)
  let sort: SortOption | undefined = undefined;
  const sf = params.get(sortFieldParam);
  const sd = params.get(sortDirParam) as SortDirection | null;
  if (sf && sd && (sd === 'asc' || sd === 'desc')) {
    sort = { field: sf, direction: sd };
  }

  if (Object.keys(columnFilters).length === 0 && !sort) return null;
  return { columnFilters, sort };
}

/**
 * Writes discrete params for the supported subset into the provided URLSearchParams.
 * Existing values for those keys are removed/updated. Returns the mutated instance for chaining.
 */
export function serializeDiscreteParams(
  params: URLSearchParams,
  state: AdvancedFilterQueryState,
  cfg?: DiscreteParamConfig
): URLSearchParams {
  const { merchantNameParam, approvedAmountMinParam, approvedAmountMaxParam, sortFieldParam, sortDirParam } =
    {
      ...defaultDiscreteParamConfig,
      ...(cfg || {}),
    };

  // Clear existing keys first
  params.delete(merchantNameParam);
  params.delete(approvedAmountMinParam);
  params.delete(approvedAmountMaxParam);
  params.delete(sortFieldParam);
  params.delete(sortDirParam);

  // merchantName
  type MerchantFilterValues = { selectedValues?: Array<{ id: string; label?: string }> } | undefined;
  const mn = state.columnFilters?.merchantName as MerchantFilterValues;
  const merchantSelected: { id: string; label?: string }[] = mn?.selectedValues || [];
  merchantSelected.forEach((opt) => {
    if (opt && typeof opt.id === 'string' && opt.id.length) {
      params.append(merchantNameParam, opt.id);
    }
  });

  // approvedAmountValue
  type AmountFilterValues = { minNumber?: number; maxNumber?: number } | undefined;
  const av = state.columnFilters?.approvedAmountValue as AmountFilterValues;
  const min = av?.minNumber;
  const max = av?.maxNumber;
  if (typeof min === 'number' && !Number.isNaN(min)) {
    params.set(approvedAmountMinParam, String(min));
  }
  if (typeof max === 'number' && !Number.isNaN(max)) {
    params.set(approvedAmountMaxParam, String(max));
  }

  // Generic multi-value ids
  const writeMulti = (key: string) => {
    params.delete(key);
    params.delete(ruleParamFor(key));
    const filter = state.columnFilters?.[key] as
      | { selectedValues?: Array<{ id: string }>; rule?: string }
      | undefined;
    const f = filter?.selectedValues || [];
    f.forEach((opt) => {
      if (opt && typeof opt.id === 'string' && opt.id.length) params.append(key, opt.id);
    });
    if (params.has(key) && filter?.rule === EXCLUDE_RULE) params.set(ruleParamFor(key), EXCLUDE_RULE);
  };

  multiKeys.forEach(writeMulti);

  // sort
  if (state.sort) {
    params.set(sortFieldParam, state.sort.field);
    params.set(sortDirParam, state.sort.direction);
  }

  return params;
}

/**
 * Clears all filter-related query parameters from the URL.
 * This includes all discrete params (merchantName, approvedAmount, multiKeys, sort).
 * Use this when you need to ensure the URL is completely cleared of filters.
 */
export function clearAllFilterParams(params: URLSearchParams, cfg?: DiscreteParamConfig): URLSearchParams {
  const { merchantNameParam, approvedAmountMinParam, approvedAmountMaxParam, sortFieldParam, sortDirParam } =
    {
      ...defaultDiscreteParamConfig,
      ...(cfg || {}),
    };

  // Clear main params
  params.delete(merchantNameParam);
  params.delete(approvedAmountMinParam);
  params.delete(approvedAmountMaxParam);
  params.delete(sortFieldParam);
  params.delete(sortDirParam);
  params.delete('filter');

  // Clear all multiKeys
  multiKeys.forEach((key) => {
    params.delete(key);
    params.delete(ruleParamFor(key));
  });

  return params;
}

/**
 * Clears all filter parameters from the current URL and updates the browser history.
 * This is a convenience function that handles the full URL manipulation including:
 * - Reading current URL search params
 * - Clearing all filter params
 * - Preserving hash
 * - Updating browser history
 *
 * @param paramKey - Optional JSON param key to also clear (default: 'af')
 * @param cfg - Optional discrete param configuration
 */
export function clearFiltersFromCurrentUrl(paramKey = 'af', cfg?: DiscreteParamConfig): void {
  const params = new URLSearchParams(window.location.search);

  // Clear the JSON param if it exists
  if (params.has(paramKey)) {
    params.delete(paramKey);
  }

  // Clear all discrete filter params
  clearAllFilterParams(params, cfg);

  // Update URL
  const qs = params.toString();
  const hash = window.location.hash || '';
  const nextUrl = qs ? `${window.location.pathname}?${qs}${hash}` : `${window.location.pathname}${hash}`;
  window.history.replaceState(null, '', nextUrl);
}
